import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

/** Fullscreen digital-signage player — public.
 *  Fetches /signage/play/{token}, auto-cycles through the resolved playlist.
 *  Re-fetches every minute so schedule changes are picked up.
 *  Pings the heartbeat endpoint every 30s (with the current media_id).
 */
export default function PlayScreen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState(null);
  const [muted, setMuted] = useState(true);
  const [flash, setFlash] = useState(null);
  const timerRef = useRef(null);
  const refetchRef = useRef(null);
  const heartbeatRef = useRef(null);
  const cmdPollRef = useRef(null);
  const currentMediaIdRef = useRef(null);
  const itemsRef = useRef([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/signage/play/${token}`);
      setData(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Screen not found");
    }
  };

  const sendHeartbeat = async (media_id) => {
    try {
      await api.post(`/signage/play/${token}/heartbeat`, { media_id: media_id || null });
    } catch (e) {
      // Silent — player should never visually fail due to analytics
    }
  };

  const showFlash = (label) => {
    setFlash(label);
    setTimeout(() => setFlash(null), 1600);
  };

  const pollCommands = async () => {
    try {
      const { data } = await api.get(`/signage/play/${token}/commands`);
      const cmds = data?.commands || [];
      for (const cmd of cmds) {
        const len = itemsRef.current.length;
        switch (cmd.action) {
          case "next":
            if (len) setIdx((i) => (i + 1) % len);
            showFlash("Next");
            break;
          case "prev":
            if (len) setIdx((i) => (i - 1 + len) % len);
            showFlash("Previous");
            break;
          case "reload":
            showFlash("Reloading");
            await load();
            break;
          case "mute":
            setMuted(true);
            showFlash("Muted");
            break;
          case "unmute":
            setMuted(false);
            showFlash("Unmuted");
            break;
          default:
            break;
        }
      }
    } catch (e) {
      // Silent
    }
  };

  useEffect(() => {
    load();
    refetchRef.current = setInterval(load, 60000);
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat(currentMediaIdRef.current);
    }, 30000);
    cmdPollRef.current = setInterval(pollCommands, 3000);
    return () => {
      clearInterval(refetchRef.current);
      clearInterval(heartbeatRef.current);
      clearInterval(cmdPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const items = data?.playlist?.resolved_items || [];
  itemsRef.current = items;
  const current = items[idx];

  useEffect(() => {
    if (!current) {
      currentMediaIdRef.current = null;
      return;
    }
    currentMediaIdRef.current = current.id;
    // Fire a play-log event the moment a new media starts
    sendHeartbeat(current.id);
    const ms = (current.duration_sec || 10) * 1000;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIdx((i) => (items.length ? (i + 1) % items.length : 0));
    }, ms);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, items.length]);

  // Reset idx when playlist changes
  useEffect(() => {
    setIdx(0);
  }, [data?.playlist?.id]);

  // Hide the browser cursor for a true "display" feel
  useEffect(() => {
    document.documentElement.style.cursor = "none";
    return () => {
      document.documentElement.style.cursor = "";
    };
  }, []);

  if (err) {
    return (
      <div className="fixed inset-0 bg-[#0A0908] text-white flex items-center justify-center p-8 text-center">
        <div>
          <div className="text-[#E8D6A3] text-xs uppercase tracking-[0.2em] mb-2">
            Mirrorloops Signage
          </div>
          <div className="text-3xl font-black">Screen not registered</div>
          <div className="text-zinc-500 mt-2">Token: {token}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="fixed inset-0 bg-[#0A0908] text-zinc-500 flex items-center justify-center">
        Loading…
      </div>
    );
  }

  if (!current) {
    return (
      <div className="fixed inset-0 bg-[#0A0908] text-white flex items-center justify-center p-8 text-center">
        <div>
          <div className="text-[#E8D6A3] text-xs uppercase tracking-[0.2em] mb-2">
            Mirrorloops Signage
          </div>
          <div className="text-3xl font-black">{data.screen.name}</div>
          <div className="text-zinc-500 mt-2">
            No active schedule right now.
          </div>
        </div>
        {flash && (
          <div
            data-testid="play-flash"
            className="absolute top-4 right-4 px-4 py-2 rounded-full bg-[#D4B878] text-black text-sm font-bold uppercase tracking-[0.18em] shadow-lg"
          >
            {flash}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white" data-testid="play-screen">
      {current.type === "video" ? (
        <video
          key={current.id}
          src={current.url}
          autoPlay
          muted={muted}
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          key={current.id}
          src={current.url}
          alt={current.name}
          className="w-full h-full object-cover"
        />
      )}
      <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur border border-white/10 text-[11px] uppercase tracking-[0.2em] text-white/60">
        Mirrorloops · {data.screen.name}
      </div>
      {flash && (
        <div
          data-testid="play-flash"
          className="absolute top-4 right-4 px-4 py-2 rounded-full bg-[#D4B878] text-black text-sm font-bold uppercase tracking-[0.18em] shadow-lg animate-[fadeIn_0.15s_ease-out]"
        >
          {flash}
        </div>
      )}
    </div>
  );
}
